/**
 * ATL-916 — read a date out of a value that has been through an unknown number of serialisations.
 *
 * WHY THIS EXISTS
 *
 * `updates.dataBefore` / `dataAfter` are opaque snapshots: whatever the record looked like at the
 * moment it was deleted, stored whole. Nothing normalises what is INSIDE them, so a nested date
 * arrives in a different shape depending on which store answered and who wrote the row:
 *
 *   Firestore read              a real Timestamp                     → `.toDate()`
 *   Supabase, migrated row      `{ _seconds, _nanoseconds }`         the admin SDK's JSON encoding
 *                                                                    of a Timestamp, written by
 *                                                                    `migration.service.ts`
 *   Supabase, live dual-write   `"2026-07-21T13:07:02.705Z"`         `dualWriteUpdate` posts JSON,
 *                                                                    so Dates become ISO strings
 *   Supabase, written by an RPC `"2026-09-09 23:29:55.534+00"`       a `timestamptz` cast with
 *                                                                    `::TEXT` inside
 *                                                                    `jsonb_build_object` (ATL-1313)
 *
 * The consumer (`screens/Updates/UpdatesScreen.js`) called `.toDate()` on it directly. That is
 * correct against Firestore and a TypeError against BOTH Supabase shapes — so switching an org's
 * reads to Supabase crashes the Updates screen on the first `deleteSale` or `deleteWorkingHour`
 * row it renders. `OperationsList.transformApiItemToFirestoreFormat` does not help: it converts the
 * one TOP-LEVEL date field it is told about (`date`), never anything nested inside a snapshot.
 *
 * The fix belongs here rather than in the transform. A transform that walked the snapshot looking
 * for date-shaped values would have to guess which strings are dates — inside a cart, over product
 * names and free-text comments — and guessing wrong rewrites data. At the call site the semantics
 * are known: this field IS a date, decode it in whichever encoding it arrived.
 *
 * Returns `null` rather than throwing or inventing `new Date()`. A snapshot with an unreadable date
 * must render as an unknown date; substituting the wall clock is the ATL-868 `convertTimestamp`
 * mistake, which fabricated 415 `last_change_at` values that then read as real.
 *
 * ATL-1313 — THE FOURTH ENCODING, AND WHY IT NEEDS CODE RATHER THAN `new Date`
 *
 * `to_jsonb(timestamptz)` emits ISO-8601 and would have been fine. The supply RPCs instead wrote
 * `jsonb_build_object('startDate', v_supply_date::TEXT, …)`, and `::TEXT` renders a `timestamptz`
 * in POSTGRES's output format — a space instead of `T`, and an offset truncated to `+00`. That is
 * not an ECMAScript Date Time String, so parsing it is left to each engine's fallback:
 *
 *   V8 (web, node, and every QA browser)   parses it, correct instant
 *   Hermes (iOS and Android native)        `Invalid Date`
 *
 * `date-fns@2` answers an invalid date by THROWING `RangeError: Invalid time value`, and the
 * payments list formats these values inside JSX — so on native the throw is a React render error,
 * which is fatal, which relaunches the app, which reopens the list. A crash loop that no browser
 * can reproduce (ATL-1313: 584 such rows across 36 organizations, 116 of them already cut over).
 *
 * Measured against Hermes 0.13 rather than read off the spec, because the rejected element is not
 * the obvious one — it is the truncated offset, not the space:
 *
 *   `"2026-09-09T23:29:55.534+00"`      T, two-digit offset   → Invalid Date
 *   `"2026-09-09 23:29:55.534+00:00"`   space, full offset    → parses
 *
 * So the normalisation below runs BEFORE `new Date`, not as a fallback after it fails. A fallback
 * would leave web on V8's lenient guess and native on this code — two paths, one of which no test
 * running under node can execute. One deterministic path is the point.
 */

/** A Firestore Timestamp, or anything shaped enough like one to answer `toDate()`. */
interface TimestampLike {
  toDate: () => Date
}

/** The admin SDK's JSON encoding of a Timestamp, in both the underscored and plain spellings. */
interface SerializedTimestamp {
  _seconds?: unknown
  _nanoseconds?: unknown
  seconds?: unknown
  nanoseconds?: unknown
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/**
 * ATL-1313 — the Postgres-`timestamptz` decoder lives in `@atlas/api-client`, not here.
 *
 * `deserializeDates` is the FIRST thing to see one of these strings and the LAST thing that still
 * has it, so the normalisation has to run there or the string is already gone by the time this
 * module is reached. Importing the same function rather than keeping a second copy is what stops
 * the two layers drifting — a decoder that disagrees with the one upstream of it is worse than
 * either alone.
 */
import { postgresTimestampToIso } from '@atlas/api-client'

// Re-exported so the existing tests and any future consumer can reach it from here too.
export { postgresTimestampToIso }


/**
 * Decode a possibly-nested date value into a `Date`, or `null` when it does not carry one.
 *
 * Order matters: `toDate()` is tried first because a real Timestamp also exposes `seconds`, and the
 * SDK's own conversion is authoritative over re-deriving it from the parts.
 */
export function toNestedDate(value: unknown): Date | null {
  if (value == null) return null

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  if (typeof value === 'object' && typeof (value as TimestampLike).toDate === 'function') {
    try {
      const d = (value as TimestampLike).toDate()
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null
    } catch {
      // A `toDate` that throws is not a Timestamp; fall through to the other encodings rather than
      // letting one malformed snapshot field take down the whole list.
      return null
    }
  }

  if (typeof value === 'object') {
    const t = value as SerializedTimestamp
    const seconds = isFiniteNumber(t._seconds) ? t._seconds : isFiniteNumber(t.seconds) ? t.seconds : null
    if (seconds !== null) {
      const nanos = isFiniteNumber(t._nanoseconds)
        ? t._nanoseconds
        : isFiniteNumber(t.nanoseconds)
          ? t.nanoseconds
          : 0
      const d = new Date(seconds * 1000 + Math.floor(nanos / 1e6))
      return Number.isNaN(d.getTime()) ? null : d
    }
    return null
  }

  if (typeof value === 'string' || typeof value === 'number') {
    // A bare number is milliseconds since the epoch — the shape `JSON.stringify(date.getTime())`
    // produces. Seconds-vs-milliseconds is NOT guessed from magnitude: that heuristic silently
    // reinterprets any timestamp before 1970-01-01T00:16:40Z, and no such date is legitimate here.
    //
    // ATL-1313: a string is normalised FIRST, so a Postgres-rendered `timestamptz` takes the same
    // code path on Hermes as on V8. Anything that is not one falls through unchanged.
    const normalised = typeof value === 'string' ? (postgresTimestampToIso(value) ?? value) : value
    const d = new Date(normalised)
    return Number.isNaN(d.getTime()) ? null : d
  }

  return null
}
